/**
 * `clip.json` — the small metadata object uploaded beside every shared clip.
 *
 * Layout strings (names, ranks, icon URLs) are resolved at share time so the
 * viewer stays mostly game-agnostic. League item / spell / rune *tooltips* are
 * the exception: icons carry only a `tipId`, and the website fetches Data
 * Dragon on hover using `match.ddragonVersion`.
 *
 * From v5 the wire form interns repeated icons (spells, runes, items, portraits,
 * crests) into `match.icons` and references them by id. `parseClipSidecar`
 * always expands those refs so consumers see fully hydrated rows; call
 * `compactClipSidecar` before upload to write the deduped form.
 *
 * Consumed by both the desktop app (writer) and the website (reader) — keep it
 * dependency-free and treat every field as untrusted when reading.
 */

export const CLIP_SIDECAR_VERSION = 5 as const;

/** Emphasis for a derived figure. The app decides; the site only picks a colour. */
export type ClipTone = "default" | "good" | "great" | "bad";

/** A single labelled value: one KPI tile, or one scoreboard cell. */
export type ClipStat = {
  label: string;
  value: string;
  /** Second line under the value, e.g. "7.00 KDA" beneath "12/3/9". */
  sub?: string | null;
  /** Emphasis applied to `sub`. */
  tone?: ClipTone | null;
  /** Longer form shown on hover, e.g. the exact number behind "12.4k". */
  hint?: string | null;
};

/**
 * A run of tooltip text in one colour.
 *
 * Built on the website from Data Dragon HTML (League tips). Hex rather than a
 * Tailwind class: the palette comes from Riot tags at runtime.
 */
export type ClipTextSpan = {
  text: string;
  /** `#rrggbb`. Omitted means the tooltip's own foreground colour. */
  color?: string | null;
  bold?: boolean;
};

/** Rich hover card for an icon — assembled on the website from Data Dragon. */
export type ClipTipCard = {
  title: string;
  /** Small label above the title, e.g. "Jungle Quest Reward". */
  eyebrow?: string | null;
  /** Gold cost line, when the thing has one. */
  cost?: { total: number; base: number } | null;
  /** Description, already parsed into coloured runs. */
  body?: ClipTextSpan[];
};

/**
 * Tip identity for on-hover Data Dragon lookup.
 *
 * Shapes: `item:<id>`, `item:<id>:quest`, `spell:<key>`, `rune:<id>`,
 * `rune:<id>:keystone`, `rune:<id>:secondary`.
 */
export type ClipTipId = string;

/** Key into `match.icons` on the v5 wire form. */
export type ClipIconId = string;

/** One CDN image. `null` in a slot list means a deliberately empty slot. */
export type ClipIcon = {
  url: string;
  /** Short label used as alt text and as the plain hover title while tips load. */
  alt?: string | null;
  /** League tip identity — see `ClipTipId`. Website fetches the card on hover. */
  tipId?: ClipTipId | null;
};

/** Icons stacked into one vertical column beside the portrait. */
export type ClipIconGroup = {
  icons: (ClipIcon | null)[];
};

/** One row of the scoreboard. */
export type ClipPlayerRow = {
  name: string;
  /** Riot-style tag shown dimmed after the name, e.g. "EUW". No leading "#". */
  tag?: string | null;
  /** Champion / agent / unit portrait. Absolute https URL on a public CDN. */
  iconUrl?: string | null;
  /** Corner badge burned onto the portrait, e.g. the champion's level. */
  iconBadge?: string | null;
  /** Secondary line under the name, e.g. a rank or a role. */
  detail?: string | null;
  /** Small icon before `detail`, e.g. a ranked crest. */
  detailIconUrl?: string | null;
  /** The person who recorded the clip — highlighted in the table. */
  isLocal?: boolean;
  /**
   * Loadout columns rendered between the portrait and the name: summoner
   * spells in one column, rune trees in the next. Each group is one column.
   */
  loadout?: ClipIconGroup[];
  /**
   * Final build. Rendered on a fixed four-wide grid in the order given, so the
   * app controls slot placement (including which slot holds the quest reward).
   */
  items?: (ClipIcon | null)[];
  /** Columns, in display order. Every row in a team should use the same labels. */
  cells: ClipStat[];
};

export type ClipTeam = {
  /** "Blue side", "Attackers", "Lobby" — whatever the game calls it. */
  name: string;
  won?: boolean | null;
  /** Headline number for the team: rounds won, kill total, placement. */
  score?: string | null;
  /** Right-aligned team totals line, e.g. "33 kills · 68.4k gold". */
  summary?: string | null;
  players: ClipPlayerRow[];
};

export type ClipMatchResult = "win" | "loss" | "draw" | "remake";

/**
 * Presentation-ready match summary. Every string is already localised,
 * abbreviated, and formatted — the website never does arithmetic on these.
 */
export type ClipMatchSummary = {
  result?: ClipMatchResult | null;
  /** Queue / mode label, e.g. "Ranked Solo/Duo" or "Competitive". */
  queue?: string | null;
  /** Match length in seconds — rendered as the "12:34" chip. */
  durationSec?: number | null;
  playedAtMs?: number | null;
  /**
   * Riot platform id (e.g. "euw1") — lets the viewer build u.gg / op.gg profile
   * URLs from each player's name#tag. Omitted for non-League games.
   */
  platformId?: string | null;
  /**
   * Data Dragon patch used for League tip fetches (e.g. "16.14.1"). Written at
   * share time so hover cards match the icons already on the scoreboard.
   */
  ddragonVersion?: string | null;
  /**
   * Deduped icon dictionary on the v5 *wire* form only. `parseClipSidecar`
   * expands refs into player rows and drops this field, so hydrated sidecars
   * (what the website/app consume) never carry it.
   */
  icons?: Record<ClipIconId, ClipIcon>;
  /** Headline identity: the champion, agent, or comp the clip is about. */
  subject?: {
    name: string;
    detail?: string | null;
    iconUrl?: string | null;
  } | null;
  /** Big tiles above the scoreboard. Keep to six or fewer. */
  stats?: ClipStat[];
  /** Optional full scoreboard; one entry per team, or a single lobby table. */
  teams?: ClipTeam[];
};

export type ClipGame = {
  /** OpenVOD game id, e.g. "league-of-legends". Used only as a display key. */
  id: string;
  name: string;
};

export type ClipSidecar = {
  /** Absent on the first-generation sidecar, which had no `v` field. */
  v?: number;
  id: string;
  title: string;
  /** Original filename, kept for the download name. */
  file: string;
  sizeBytes: number;
  contentType: string;
  createdAtMs: number;
  /** Clip length in seconds, when it could be probed. */
  durationSec?: number | null;
  /**
   * When the owner set the share to auto-delete, so the viewer can show a
   * "removed in N days" note. Absent means it stays up until stopped.
   */
  expiresAtMs?: number | null;
  game?: ClipGame | null;
  match?: ClipMatchSummary | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const str = (value: unknown, max = 200): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
};

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Only https CDN images are allowed through — a sidecar is a remote file. */
const imageUrl = (value: unknown): string | null => {
  const raw = str(value, 2048);
  if (!raw) return null;
  try {
    return new URL(raw).protocol === "https:" ? raw : null;
  } catch {
    return null;
  }
};

const parseTone = (value: unknown): ClipTone | null =>
  value === "good" || value === "great" || value === "bad" || value === "default" ? value : null;

const parseStat = (value: unknown): ClipStat | null => {
  if (!isRecord(value)) return null;
  const label = str(value.label, 40);
  const text = str(value.value, 60);
  if (!label || !text) return null;
  return {
    label,
    value: text,
    sub: str(value.sub, 40),
    tone: parseTone(value.tone),
    hint: str(value.hint, 160)
  };
};

/** Nulls survive: an empty item slot is meaningful and must keep its position. */
const parseIcon = (value: unknown): ClipIcon | null => {
  if (!isRecord(value)) return null;
  const url = imageUrl(value.url);
  if (!url) return null;
  return {
    url,
    alt: str(value.alt, 80),
    tipId: str(value.tipId, 80)
  };
};

const parseIconDict = (value: unknown): Record<ClipIconId, ClipIcon> => {
  if (!isRecord(value)) return {};
  const out: Record<ClipIconId, ClipIcon> = {};
  for (const [rawKey, rawIcon] of Object.entries(value)) {
    const key = str(rawKey, 80);
    const icon = parseIcon(rawIcon);
    if (key && icon) out[key] = icon;
  }
  return out;
};

/**
 * Resolve a slot that may be an inline icon (v4), a dictionary key (v5), or null.
 * Unknown keys become null rather than leaking raw strings into the UI.
 */
const resolveIconSlot = (value: unknown, icons: Record<ClipIconId, ClipIcon>): ClipIcon | null => {
  if (value == null) return null;
  if (typeof value === "string") {
    const key = str(value, 80);
    return key ? (icons[key] ?? null) : null;
  }
  return parseIcon(value);
};

const parseIconSlots = (
  value: unknown,
  max: number,
  icons: Record<ClipIconId, ClipIcon>
): (ClipIcon | null)[] => {
  if (!Array.isArray(value)) return [];
  return value.slice(0, max).map((slot) => resolveIconSlot(slot, icons));
};

const parseIconGroups = (value: unknown, icons: Record<ClipIconId, ClipIcon>): ClipIconGroup[] => {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, 4)
    .map((group) => (isRecord(group) ? { icons: parseIconSlots(group.icons, 4, icons) } : null))
    .filter((group): group is ClipIconGroup => group !== null && group.icons.length > 0);
};

const parseStats = (value: unknown, max: number): ClipStat[] => {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, max)
    .map(parseStat)
    .filter((stat): stat is ClipStat => stat !== null);
};

const parseResult = (value: unknown): ClipMatchResult | null =>
  value === "win" || value === "loss" || value === "draw" || value === "remake" ? value : null;

/** Portrait / crest: absolute URL, or a key into the icons dict. */
const resolveImageRef = (
  ref: unknown,
  legacyUrl: unknown,
  icons: Record<ClipIconId, ClipIcon>
): string | null => {
  const fromLegacy = imageUrl(legacyUrl);
  if (fromLegacy) return fromLegacy;
  if (typeof ref === "string") {
    const asUrl = imageUrl(ref);
    if (asUrl) return asUrl;
    const key = str(ref, 80);
    if (key && icons[key]) return icons[key].url;
  }
  return null;
};

const parsePlayer = (value: unknown, icons: Record<ClipIconId, ClipIcon>): ClipPlayerRow | null => {
  if (!isRecord(value)) return null;
  const name = str(value.name, 80);
  if (!name) return null;
  return {
    name,
    tag: str(value.tag, 16),
    iconUrl: resolveImageRef(value.icon, value.iconUrl, icons),
    iconBadge: str(value.iconBadge, 4),
    detail: str(value.detail, 60),
    detailIconUrl: resolveImageRef(value.detailIcon, value.detailIconUrl, icons),
    isLocal: value.isLocal === true,
    loadout: parseIconGroups(value.loadout, icons),
    items: parseIconSlots(value.items, 8, icons),
    cells: parseStats(value.cells, 8)
  };
};

const parseTeam = (value: unknown, icons: Record<ClipIconId, ClipIcon>): ClipTeam | null => {
  if (!isRecord(value)) return null;
  const name = str(value.name, 40);
  if (!name) return null;
  const players = Array.isArray(value.players)
    ? value.players
        .slice(0, 16)
        .map((row) => parsePlayer(row, icons))
        .filter((row): row is ClipPlayerRow => row !== null)
    : [];
  return {
    name,
    won: typeof value.won === "boolean" ? value.won : null,
    score: str(value.score, 20),
    summary: str(value.summary, 60),
    players
  };
};

const parseMatch = (value: unknown): ClipMatchSummary | null => {
  if (!isRecord(value)) return null;
  const icons = parseIconDict(value.icons);
  const subjectRaw = isRecord(value.subject) ? value.subject : null;
  const subjectName = subjectRaw ? str(subjectRaw.name, 80) : null;
  const teams = Array.isArray(value.teams)
    ? value.teams
        .slice(0, 4)
        .map((team) => parseTeam(team, icons))
        .filter((team): team is ClipTeam => team !== null)
    : [];
  const match: ClipMatchSummary = {
    result: parseResult(value.result),
    queue: str(value.queue, 40),
    durationSec: num(value.durationSec),
    playedAtMs: num(value.playedAtMs),
    platformId: str(value.platformId, 16),
    ddragonVersion: str(value.ddragonVersion, 24),
    subject:
      subjectRaw && subjectName
        ? {
            name: subjectName,
            detail: str(subjectRaw.detail, 60),
            iconUrl: resolveImageRef(subjectRaw.icon, subjectRaw.iconUrl, icons)
          }
        : null,
    stats: parseStats(value.stats, 6),
    teams
  };
  const hasAnything =
    match.result !== null ||
    match.queue !== null ||
    match.subject !== null ||
    match.stats!.length > 0 ||
    teams.length > 0;
  return hasAnything ? match : null;
};

/**
 * Validate and normalise a fetched `clip.json`.
 *
 * Returns null when the object is unrecognisable. Anything short of that is
 * kept: a first-generation sidecar with no `v` and no match block still yields
 * a playable clip, which is the whole point of versioning it loosely.
 *
 * v5 icon dictionaries are expanded into player rows here, so callers never
 * have to resolve refs themselves.
 */
export const parseClipSidecar = (value: unknown): ClipSidecar | null => {
  if (!isRecord(value)) return null;
  const id = str(value.id, 64);
  if (!id) return null;
  const file = str(value.file, 260) ?? `${id}.mp4`;
  const gameRaw = isRecord(value.game) ? value.game : null;
  const gameId = gameRaw ? str(gameRaw.id, 40) : null;
  return {
    v: num(value.v) ?? 1,
    id,
    title: str(value.title, 200) ?? file.replace(/\.[a-z0-9]+$/i, ""),
    file,
    sizeBytes: num(value.sizeBytes) ?? 0,
    contentType: str(value.contentType, 80) ?? "video/mp4",
    createdAtMs: num(value.createdAtMs) ?? 0,
    durationSec: num(value.durationSec),
    expiresAtMs: num(value.expiresAtMs),
    game: gameRaw && gameId ? { id: gameId, name: str(gameRaw.name, 60) ?? gameId } : null,
    match: parseMatch(value.match)
  };
};

// --- compact (writer) --------------------------------------------------------

/** Prefer tipId; otherwise a stable key from the URL path (champion:Ahri, …). */
const iconDictKey = (icon: ClipIcon, fallback: () => string): ClipIconId => {
  const tip = icon.tipId?.trim();
  if (tip) return tip.length > 80 ? tip.slice(0, 80) : tip;
  try {
    const url = new URL(icon.url);
    const parts = url.pathname.split("/").filter(Boolean);
    const file = (parts.at(-1) ?? "x").replace(/\.[^.]+$/, "");
    const parent = parts.at(-2) ?? "img";
    const key = `${parent}:${file}`;
    return key.length > 80 ? key.slice(0, 80) : key;
  } catch {
    return fallback();
  }
};

type IconIntern = {
  icons: Record<ClipIconId, ClipIcon>;
  intern: (icon: ClipIcon | null | undefined) => ClipIconId | null;
  internUrl: (url: string | null | undefined, alt?: string | null) => ClipIconId | null;
};

const createIconIntern = (): IconIntern => {
  const icons: Record<ClipIconId, ClipIcon> = {};
  let seq = 0;
  const alloc = (): string => {
    seq += 1;
    return `i${seq}`;
  };

  const intern = (icon: ClipIcon | null | undefined): ClipIconId | null => {
    if (!icon?.url) return null;
    const key = iconDictKey(icon, alloc);
    const existing = icons[key];
    if (!existing) {
      icons[key] = {
        url: icon.url,
        ...(icon.alt ? { alt: icon.alt } : {}),
        ...(icon.tipId ? { tipId: icon.tipId } : {})
      };
      return key;
    }
    // Same key from tipId/url — keep the richer alt/tipId if a later copy has it.
    if (!existing.alt && icon.alt) existing.alt = icon.alt;
    if (!existing.tipId && icon.tipId) existing.tipId = icon.tipId;
    return key;
  };

  const internUrl = (url: string | null | undefined, alt?: string | null): ClipIconId | null => {
    if (!url) return null;
    return intern({ url, alt: alt ?? null, tipId: null });
  };

  return { icons, intern, internUrl };
};

const compactPlayer = (player: ClipPlayerRow, intern: IconIntern): Record<string, unknown> => {
  const row: Record<string, unknown> = {
    name: player.name,
    cells: player.cells
  };
  if (player.tag) row.tag = player.tag;
  if (player.iconBadge) row.iconBadge = player.iconBadge;
  if (player.detail) row.detail = player.detail;
  if (player.isLocal) row.isLocal = true;

  const icon = intern.internUrl(player.iconUrl, player.name);
  if (icon) row.icon = icon;

  const detailIcon = intern.internUrl(player.detailIconUrl);
  if (detailIcon) row.detailIcon = detailIcon;

  if (player.loadout && player.loadout.length > 0) {
    row.loadout = player.loadout.map((group) => ({
      icons: group.icons.map((slot) => {
        if (slot == null) return null;
        if (typeof slot === "string") return intern.intern(intern.icons[slot] ?? null) ?? slot;
        return intern.intern(slot);
      })
    }));
  }

  if (player.items && player.items.length > 0) {
    row.items = player.items.map((slot) => {
      if (slot == null) return null;
      if (typeof slot === "string") return intern.intern(intern.icons[slot] ?? null) ?? slot;
      return intern.intern(slot);
    });
  }

  return row;
};

const compactMatch = (match: ClipMatchSummary): Record<string, unknown> => {
  const intern = createIconIntern();
  const out: Record<string, unknown> = {};

  if (match.result != null) out.result = match.result;
  if (match.queue) out.queue = match.queue;
  if (match.durationSec != null) out.durationSec = match.durationSec;
  if (match.playedAtMs != null) out.playedAtMs = match.playedAtMs;
  if (match.platformId) out.platformId = match.platformId;
  if (match.ddragonVersion) out.ddragonVersion = match.ddragonVersion;
  if (match.stats && match.stats.length > 0) out.stats = match.stats;

  if (match.subject) {
    const subject: Record<string, unknown> = { name: match.subject.name };
    if (match.subject.detail) subject.detail = match.subject.detail;
    const icon = intern.internUrl(match.subject.iconUrl, match.subject.name);
    if (icon) subject.icon = icon;
    out.subject = subject;
  }

  if (match.teams && match.teams.length > 0) {
    out.teams = match.teams.map((team) => {
      const entry: Record<string, unknown> = {
        name: team.name,
        players: team.players.map((player) => compactPlayer(player, intern))
      };
      if (typeof team.won === "boolean") entry.won = team.won;
      if (team.score) entry.score = team.score;
      if (team.summary) entry.summary = team.summary;
      return entry;
    });
  }

  if (Object.keys(intern.icons).length > 0) out.icons = intern.icons;
  return out;
};

/**
 * Rewrite a hydrated sidecar into the deduped v5 wire form.
 *
 * Safe to call with already-compact input. Returns a plain JSON-ready object
 * (icon slots are string ids; portraits use `icon` / `detailIcon` refs).
 */
export const compactClipSidecar = (sidecar: ClipSidecar): Record<string, unknown> => {
  const out: Record<string, unknown> = {
    v: CLIP_SIDECAR_VERSION,
    id: sidecar.id,
    title: sidecar.title,
    file: sidecar.file,
    sizeBytes: sidecar.sizeBytes,
    contentType: sidecar.contentType,
    createdAtMs: sidecar.createdAtMs
  };
  if (sidecar.durationSec != null) out.durationSec = sidecar.durationSec;
  if (sidecar.expiresAtMs != null) out.expiresAtMs = sidecar.expiresAtMs;
  if (sidecar.game) out.game = sidecar.game;
  if (sidecar.match) out.match = compactMatch(sidecar.match);
  return out;
};
